import React, { useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Nav, Navbar, Row, Col } from 'react-bootstrap';
import { navbarBreakPoint } from 'config';
import Flex from 'components/common/Flex';
import Logo from 'components/common/Logo';
import NavbarVerticalMenu from './NavbarVerticalMenu';
import ToggleButton from './ToggleButton';
import { capitalize } from 'helpers/utils';
import bgNavbar from 'assets/img/generic/bg-navbar.png';
import { useAppContext } from 'providers/AppProvider';
import { useAuth } from 'contexts/AuthContext';
import Loading from 'components/common/Loading';

const NavbarVertical = () => {
  const {
    config: { navbarStyle, isNavbarVerticalCollapsed, showBurgerMenu }
  } = useAppContext();

  const { userMenus, menusLoading, authenticated, user, hasRole } = useAuth();

  // Filtrar menús para clientes (ocultar Infomedia y Administración)
  const isClient = user?.assignedClientIds?.length > 0 && 
    !hasRole('admin') && 
    !hasRole('super_admin') &&
    !hasRole('MANAGER') &&
    !user?.isSuperAdmin;

  const filteredMenus = React.useMemo(() => {
    if (!isClient) return userMenus;
    
    // Si es cliente, ocultar Infomedia y Administración
    return userMenus.filter(menu => 
      !['Infomedia', 'Administración', 'Admin'].includes(menu.name)
    ).map(menu => {
      // También filtrar children si existen
      if (menu.children) {
        return {
          ...menu,
          children: menu.children.filter(child => 
            !['Infomedia', 'Administración', 'Admin'].includes(child.name)
          )
        };
      }
      return menu;
    });
  }, [userMenus, isClient]);
  

  const HTMLClassList = document.getElementsByTagName('html')[0].classList;

  useEffect(() => {
    if (isNavbarVerticalCollapsed) {
      HTMLClassList.add('navbar-vertical-collapsed');
    } else {
      HTMLClassList.remove('navbar-vertical-collapsed');
    }
    return () => {
      HTMLClassList.remove('navbar-vertical-collapsed-hover');
    };
  }, [isNavbarVerticalCollapsed, HTMLClassList]);

  //Control mouseEnter event
  let time = null;
  const handleMouseEnter = () => {
    if (isNavbarVerticalCollapsed) {
      time = setTimeout(() => {
        HTMLClassList.add('navbar-vertical-collapsed-hover');
      }, 100);
    }
  };
  const handleMouseLeave = () => {
    clearTimeout(time);
    HTMLClassList.remove('navbar-vertical-collapsed-hover');
  };

  const NavbarLabel = ({ label }) => (
    <Nav.Item as="li">
      <Row className="mt-3 mb-2 navbar-vertical-label-wrapper">
        <Col xs="auto" className="navbar-vertical-label navbar-vertical-label">
          {label}
        </Col>
        <Col className="ps-0">
          <hr className="mb-0 navbar-vertical-divider"></hr>
        </Col>
      </Row>
    </Nav.Item>
  );

  // Organizar menús por secciones para mostrar con etiquetas
  const organizeMenusBySection = (menus) => {
    const sections = [];

    // Sección Principal (Dashboard)
    const principalMenus = menus.filter(menu => 
      ['Dashboard'].includes(menu.name)
    );
    if (principalMenus.length > 0) {
      sections.push({
        label: 'Principal',
        routes: principalMenus
      });
    }

    // Sección de Áreas de Negocio (Performance, Branding)
    const businessAreaMenus = menus.filter(menu => 
      ['Performance', 'Branding'].includes(menu.name)
    );
    if (businessAreaMenus.length > 0) {
      sections.push({
        label: 'Áreas de Negocio',
        routes: businessAreaMenus
      });
    }

    // Sección de Administración
    const adminMenus = menus.filter(menu => 
      ['Administración'].includes(menu.name)
    );
    if (adminMenus.length > 0) {
      sections.push({
        label: 'Administración',
        routes: adminMenus
      });
    }

    // Sección de Clientes (nueva)
    const clientMenus = menus.filter(menu => 
      ['Clients', 'Client', 'Clientes'].includes(menu.name)
    );
    if (clientMenus.length > 0) {
      sections.push({
        label: 'Clientes',
        routes: clientMenus
      });
    }

    // Sección de Aplicaciones (nueva - para Infomedia y similares)
    const appMenus = menus.filter(menu => 
      ['Infomedia', 'Apps', 'Aplicaciones', 'Data Factory', 'Ford Ventas', 'Ford Resultados', 'Ford Reportes', 'Dashboard Resultados'].includes(menu.name)
    );
    if (appMenus.length > 0) {
      sections.push({
        label: 'Aplicaciones',
        routes: appMenus
      });
    }

    // Sección de Usuario (siempre al final)
    const userMenus = menus.filter(menu => 
      ['Usuario', 'User'].includes(menu.name)
    );
    if (userMenus.length > 0) {
      sections.push({
        label: 'Usuario',
        routes: userMenus,
        labelDisable: false
      });
    }

    // FALLBACK: Cualquier menú que no esté categorizado
    const categorizedMenus = [
      ...principalMenus, 
      ...businessAreaMenus, 
      ...adminMenus, 
      ...clientMenus,
      ...appMenus,
      ...userMenus
    ];
    const uncategorizedMenus = menus.filter(menu => 
      !categorizedMenus.some(categorized => categorized.name === menu.name)
    );
    
    if (uncategorizedMenus.length > 0) {
      sections.push({
        label: 'Otros',
        routes: uncategorizedMenus
      });
    }

    return sections;
  };

  const menuSections = organizeMenusBySection(filteredMenus);
  


  return (
    <Navbar
      expand={navbarBreakPoint}
      className={classNames('navbar-vertical', {
        [`navbar-${navbarStyle}`]: navbarStyle !== 'transparent'
      })}
      variant="light"
    >
      <Flex alignItems="center">
        <ToggleButton />
        <Logo at="navbar-vertical" textClass="text-primary" width={160} />
      </Flex>
      <Navbar.Collapse
        in={showBurgerMenu}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          backgroundImage:
            navbarStyle === 'vibrant'
              ? `linear-gradient(-45deg, rgba(0, 160, 255, 0.86), #0048a2),url(${bgNavbar})`
              : 'none'
        }}
      >
        <div className="navbar-vertical-content scrollbar">
          {menusLoading ? (
            <div className="d-flex justify-content-center p-3">
              <Loading message="Cargando menús..." />
            </div>
          ) : (
            <Nav className="flex-column" as="ul">
              {menuSections.map((section, index) => (
                <Fragment key={section.label || index}>
                  {!section.labelDisable && section.routes.length > 0 && (
                    <NavbarLabel label={capitalize(section.label)} />
                  )}
                  <NavbarVerticalMenu routes={section.routes} />
                </Fragment>
              ))}

              {/* Mostrar mensaje si no hay menús */}
              {!authenticated && userMenus.length === 0 && (
                <Nav.Item as="li" className="text-center p-3">
                  <small className="text-muted">
                    Inicie sesión para ver el menú
                  </small>
                </Nav.Item>
              )}
            </Nav>
          )}
        </div>
      </Navbar.Collapse>
    </Navbar>
  );
};

NavbarVertical.propTypes = {
  label: PropTypes.string
};

export default NavbarVertical;
